// Schritt 3 des Serienmail-Assistenten — Pruefen und senden: Vorschau mit echtem
// Empfaenger (durchblaetterbar), Testmail ans eigene Postfach, Zeitpunkt, und der
// Startknopf, der an darfStarten() haengt. Die drei Bremsen aus der Spec sitzen alle
// hier: Vorschau, Testmail, Empfaengerzahl abtippen.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CaretLeft, CaretRight, CheckCircle, CircleNotch, EnvelopeSimple, PaperPlaneTilt, Timer, WarningCircle } from '@phosphor-icons/react';
import { ImmoButton } from '../../../components/immobilien/ui';
import { taktText } from '../../../components/immobilien/email/serienmailAnzeige';
import { useMobilModus } from '../../../lib/immobilien/mobil';
import { FELD, LABEL, Umschalter } from '../emails/bereich/Formular';
import { laufPfad } from './SerienmailListe';
import { supabase } from '../../../lib/supabase/client';
import { baueBriefVorschau } from '../../../lib/emails/emailHtml';
import { ersetzePlatzhalter, htmlZuText } from '../../../lib/aktionen/platzhalter';
import { baueWerte } from '../../../lib/serienmail/platzhalter';
import { darfStarten } from '../../../lib/serienmail/freigabe';
import { formatiereDatumDeutsch, legeSerienmailAn, starteVersand, type SerienmailEntwurf } from '../../../lib/serienmail/api';
import type { Kandidat } from '../../../lib/serienmail/typen';
import type { Briefpapier, Konto, SetZustand, Signatur, Zustand } from './SerienmailAssistent';
import { SerienbriefBereich } from './SerienbriefBereich';

interface SchrittPruefenProps {
  zustand: Zustand;
  setZustand: SetZustand;
  ausgewaehlt: Kandidat[];
  briefpapierListe: Briefpapier[];
  signaturenListe: Signatur[];
  konto: Konto | null;
  /** Verschluckter Ladefehler des Konto? Muss zusaetzlich zu darfStarten den Start
   *  sperren (siehe Kommentar bei `erlaubt` unten) — darfStarten selbst bleibt
   *  unveraendert, das ist getestete Logik ohne DB-Bezug. */
  kontoFehler: string | null;
  /** E-POST-Serienbrief: alle (ohne Abgewählte) und die ohne E-Mail-Adresse. */
  briefAlle: Kandidat[];
  briefOhneEmail: Kandidat[];
}

// Dieselbe Formatierung wie legeSerienmailAn in lib/serienmail/api.ts (formatiereDatumDeutsch) —
// sonst zeigt die Vorschau hier ein anderes Datum als spaeter tatsaechlich verschickt wird.
const heuteDeutsch = () => formatiereDatumDeutsch(new Date());

const SchrittPruefen: React.FC<SchrittPruefenProps> = ({ zustand, setZustand, ausgewaehlt, briefpapierListe, signaturenListe, konto, kontoFehler, briefAlle, briefOhneEmail }) => {
  const navigate = useNavigate();
  const { mobil } = useMobilModus();
  const [eigeneAdresse, setEigeneAdresse] = useState<string | null>(null);
  const [testmailStatus, setTestmailStatus] = useState<'idle' | 'senden' | 'ok' | 'fehler'>('idle');
  const [testmailFehler, setTestmailFehler] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [startFehler, setStartFehler] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setEigeneAdresse(data.user?.email ?? null));
  }, []);

  const absender = useMemo(
    () => ({ name: konto?.absender_name || konto?.bezeichnung || '', email: konto?.email_adresse || '' }),
    [konto],
  );

  const briefpapier = briefpapierListe.find((b) => b.id === zustand.briefpapierId);
  const signatur = signaturenListe.find((s) => s.id === zustand.signaturId);
  // Rohes Brief-HTML MIT Platzhaltern — das ist auch das, was legeSerienmailAn bekommt.
  // Der Worker loest die Platzhalter je Empfaenger auf (serienmail-versand/index.ts),
  // hier wird nur fuer die Vorschau/Testmail zusaetzlich aufgeloest.
  const briefHtmlRoh = baueBriefVorschau(briefpapier?.html || '', zustand.html, signatur?.html_signatur || '');

  const vorschauIndex = Math.min(zustand.vorschauIndex, Math.max(ausgewaehlt.length - 1, 0));
  const vorschauKandidat = ausgewaehlt[vorschauIndex] || null;
  const heute = heuteDeutsch();
  const vorschauWerte = vorschauKandidat ? baueWerte(vorschauKandidat, absender, heute) : null;
  const vorschauHtml = vorschauWerte ? ersetzePlatzhalter(briefHtmlRoh, vorschauWerte) : briefHtmlRoh;
  const vorschauBetreff = vorschauWerte ? ersetzePlatzhalter(zustand.betreff, vorschauWerte) : zustand.betreff;

  const blaettern = (delta: number) => {
    setZustand((z) => ({ ...z, vorschauIndex: Math.max(0, Math.min(ausgewaehlt.length - 1, z.vorschauIndex + delta)) }));
  };

  const testmailSenden = async () => {
    if (!eigeneAdresse || !vorschauKandidat || !konto) return;
    setTestmailStatus('senden');
    setTestmailFehler(null);
    try {
      const text = htmlZuText(vorschauHtml);
      const { error } = await supabase.functions.invoke('graph-mail-send', {
        body: {
          konto_id: konto.id,
          an: [eigeneAdresse],
          betreff: `[Test] ${vorschauBetreff}`,
          html_inhalt: vorschauHtml,
          text_inhalt: text,
          anhaenge: zustand.anhaenge.map((a) => ({ name: a.name, path: a.path })),
        },
      });
      if (error) throw new Error(error.message);
      setTestmailStatus('ok');
    } catch (err: any) {
      setTestmailStatus('fehler');
      setTestmailFehler(err.message || 'Testmail konnte nicht gesendet werden.');
    }
  };

  const freigabe = darfStarten(zustand.bestaetigungEingabe, ausgewaehlt.length, zustand.betreff);
  const geplantUnvollstaendig = zustand.geplantModus === 'geplant' && (!zustand.geplantDatum || !zustand.geplantZeit);
  // darfStarten() weiss nichts vom Konto — das ist reine, DB-lose Logik (getestet).
  // Ohne Konto ist absender.name/absender.email leer, und legeSerienmailAn friert
  // diese leeren Werte in JEDE Empfaengerzeile ein (siehe api.ts). Deshalb sperrt
  // `kontoFehler`/fehlendes `konto` den Start zusaetzlich, hier in der Komponente.
  const erlaubt = freigabe.erlaubt && !geplantUnvollstaendig && !kontoFehler && !!konto;
  const grund = kontoFehler
    ? 'Absender-Konto konnte nicht geladen werden — Start gesperrt, damit keine Mail ohne Absender rausgeht.'
    : !konto
    ? 'Lade Absender-Konto…'
    : geplantUnvollstaendig && freigabe.erlaubt
    ? 'Bitte Datum und Uhrzeit für den geplanten Versand angeben.'
    : freigabe.grund;

  const starten = async () => {
    if (!erlaubt) return;
    setStarting(true);
    setStartFehler(null);
    const geplantAm = zustand.geplantModus === 'geplant'
      ? new Date(`${zustand.geplantDatum}T${zustand.geplantZeit}`).toISOString()
      : null;
    const entwurf: SerienmailEntwurf = {
      betreff: zustand.betreff,
      html: briefHtmlRoh,
      vorlageId: zustand.vorlageId,
      anhaenge: zustand.anhaenge.map((a) => ({ name: a.name, path: a.path })),
      zielgruppen: zustand.zielgruppen,
      auswahl: zustand.bereich === 'alle'
        ? { alle: true, objektIds: [], einheitIds: [] }
        : { objektIds: zustand.objektIds, einheitIds: zustand.einheitIds },
      geplantAm,
      absender,
    };
    let id: string;
    try {
      id = await legeSerienmailAn(entwurf, ausgewaehlt);
    } catch (err: any) {
      // Der Lauf existiert noch nicht — hier bleibt der Nutzer im Assistenten, es gibt
      // nichts, wohin er navigieren koennte.
      setStartFehler(err.message || 'Serienmail konnte nicht angelegt werden.');
      setStarting(false);
      return;
    }
    if (zustand.geplantModus === 'sofort') {
      try {
        await starteVersand(id);
      } catch (err: any) {
        // Der Lauf steht jetzt in der DB (Status 'entwurf', lauter offene Zeilen) —
        // OHNE Navigation waere er von nirgendwo mehr erreichbar: die Detailseite
        // haette den einzig moeglichen "Jetzt starten"-Knopf, aber der Nutzer sieht
        // sie nie. Also trotz Fehler zur Detailseite, mit der Meldung im Router-State,
        // damit dort sichtbar bleibt: angelegt, aber nicht gestartet.
        navigate(laufPfad(id), {
          state: { startFehler: err.message || 'Versand konnte nicht gestartet werden.' },
        });
        return;
      }
    }
    navigate(laufPfad(id));
  };

  const karte: React.CSSProperties = { borderRadius: 24, padding: mobil ? 14 : 18, gap: 12 };
  const titel: React.CSSProperties = { fontSize: 12, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--ink3)' };
  const pfeil = (aus: boolean): React.CSSProperties => ({
    width: mobil ? 44 : 34, height: mobil ? 44 : 34, borderRadius: 11, border: 'none', background: 'var(--fill2)', color: 'var(--ink)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: aus ? 'default' : 'pointer', opacity: aus ? 0.35 : 1,
  });

  return (
    <div className="flex flex-col min-w-0" style={{ gap: 12 }}>
      {/* Vorschau */}
      <section className="immo-glas flex flex-col min-w-0" style={karte}>
        <div className="flex items-center justify-between" style={{ gap: 8 }}>
          <span style={titel}>Vorschau — echter Empfänger</span>
          {ausgewaehlt.length > 1 && (
            <div className="flex items-center" style={{ gap: 8 }}>
              <button type="button" aria-label="Vorheriger Empfänger" onClick={() => blaettern(-1)} disabled={vorschauIndex === 0} style={pfeil(vorschauIndex === 0)}><CaretLeft size={14} weight="bold" /></button>
              <span className="immo-zahl" style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink2)' }}>{vorschauIndex + 1} / {ausgewaehlt.length}</span>
              <button type="button" aria-label="Nächster Empfänger" onClick={() => blaettern(1)} disabled={vorschauIndex >= ausgewaehlt.length - 1} style={pfeil(vorschauIndex >= ausgewaehlt.length - 1)}><CaretRight size={14} weight="bold" /></button>
            </div>
          )}
        </div>

        {vorschauKandidat ? (
          <div className="flex flex-col overflow-hidden" style={{ borderRadius: 18, background: '#fff', color: '#1b2433', boxShadow: '0 18px 40px -18px var(--shade)' }}>
            <div className="flex flex-col" style={{ gap: 3, padding: '12px 16px', borderBottom: '1px solid rgba(27,36,51,0.08)' }}>
              <span style={{ fontSize: 12, color: 'rgba(27,36,51,0.6)', overflowWrap: 'anywhere' }}>
                <b style={{ color: '#1b2433' }}>An:</b> {vorschauKandidat.name} &lt;{vorschauKandidat.email}&gt;
              </span>
              <span style={{ fontSize: 15, fontWeight: 800, overflowWrap: 'anywhere' }}>{vorschauBetreff || '(ohne Betreff)'}</span>
            </div>
            <iframe title="Vorschau" srcDoc={vorschauHtml} sandbox="" className="w-full border-0 block" style={{ height: 420, background: '#fff' }} />
          </div>
        ) : (
          <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Keine Empfänger ausgewählt — keine Vorschau möglich.</span>
        )}

        {/* Testmail */}
        <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
          <ImmoButton groesse={44} icon={testmailStatus === 'senden' ? CircleNotch : EnvelopeSimple}
            onClick={testmailSenden} disabled={!eigeneAdresse || !vorschauKandidat || testmailStatus === 'senden'}
            style={{ maxWidth: '100%', whiteSpace: 'normal', textAlign: 'left' }}>
            Testmail an mich{eigeneAdresse ? ` (${eigeneAdresse})` : ''}
          </ImmoButton>
          {testmailStatus === 'ok' && (
            <span className="flex items-center" style={{ gap: 6, fontSize: 13, fontWeight: 700, color: 'var(--ok)' }}><CheckCircle size={15} weight="fill" /> Testmail gesendet</span>
          )}
          {testmailStatus === 'fehler' && (
            <span className="flex items-center" style={{ gap: 6, fontSize: 13, fontWeight: 700, color: 'var(--bad)' }}><WarningCircle size={15} weight="fill" /> {testmailFehler}</span>
          )}
        </div>
      </section>

      {/* Zeitpunkt */}
      <section className="immo-glas flex flex-col min-w-0" style={karte}>
        <span style={titel}>Zeitpunkt</span>
        <div className="self-start">
          <Umschalter hoehe={mobil ? 44 : 34} wert={zustand.geplantModus} onChange={(m) => setZustand((z) => ({ ...z, geplantModus: m }))}
            optionen={[{ key: 'sofort', label: 'Sofort' }, { key: 'geplant', label: 'Termin wählen' }]} />
        </div>
        {zustand.geplantModus === 'geplant' && (
          <div className="flex flex-wrap" style={{ gap: 10 }}>
            <input type="date" aria-label="Datum" value={zustand.geplantDatum} onChange={(e) => setZustand((z) => ({ ...z, geplantDatum: e.target.value }))} style={{ ...FELD, width: 'auto', minWidth: 170 }} />
            <input type="time" aria-label="Uhrzeit" value={zustand.geplantZeit} onChange={(e) => setZustand((z) => ({ ...z, geplantZeit: e.target.value }))} style={{ ...FELD, width: 'auto', minWidth: 120 }} />
          </div>
        )}
      </section>

      {/* Bestaetigung + Start */}
      <section className="immo-glas flex flex-col min-w-0" style={karte}>
        <span style={titel}>Bestätigung</span>
        <label className="flex flex-col" style={{ gap: 8 }}>
          <span style={{ ...LABEL, fontWeight: 600, color: 'var(--ink2)' }}>
            Zur Bestätigung die Empfängerzahl <b className="immo-zahl" style={{ color: 'var(--ink)' }}>{ausgewaehlt.length}</b> eintragen:
          </span>
          <input
            type="text"
            inputMode="numeric"
            value={zustand.bestaetigungEingabe}
            onChange={(e) => setZustand((z) => ({ ...z, bestaetigungEingabe: e.target.value }))}
            placeholder={String(ausgewaehlt.length)}
            style={{ ...FELD, width: 160, fontWeight: 800, fontSize: mobil ? 16 : 14 }}
          />
        </label>

        {startFehler && (
          <div role="alert" className="flex items-center" style={{ gap: 8, borderRadius: 14, padding: '10px 14px', background: 'var(--bad-soft)', color: 'var(--bad)', fontSize: 13, fontWeight: 700 }}>
            <WarningCircle size={15} weight="bold" className="shrink-0" /> {startFehler}
          </div>
        )}

        <div className="flex flex-wrap items-center" style={{ gap: 12 }}>
          <ImmoButton art="akzent" groesse={48} icon={starting ? CircleNotch : PaperPlaneTilt} onClick={starten} disabled={!erlaubt || starting} style={{ opacity: !erlaubt || starting ? 0.45 : 1 }}>
            {zustand.geplantModus === 'sofort' ? 'Jetzt senden' : 'Serienmail planen'}
          </ImmoButton>
          {!erlaubt && grund && <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--warn)' }}>{grund}</span>}
        </div>
        {/* Entscheidung 11: Info zum festen Versandtakt (20/min sitzt in der Edge Function). */}
        <span className="flex items-center" style={{ gap: 6, fontSize: 12, color: 'var(--ink3)' }}>
          <Timer size={14} />{taktText(ausgewaehlt.length)}
        </span>
      </section>

      <SerienbriefBereich alle={briefAlle} ohneEmail={briefOhneEmail} betreff={zustand.betreff} html={zustand.html}
        anhaenge={zustand.anhaenge} absender={absender} />
    </div>
  );
};

export default SchrittPruefen;
